var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, CircularProgress } from "@mui/material";
import { Chart } from "react-google-charts";
import { CaretLeft, CaretRight, ChartBar } from "@phosphor-icons/react";
import { SHORT_MONTH_NAMES, formatISOToShortDate, formatISOToShortDateNoYear, extractDateFromISO, parseISODateParts, clampDate, } from "../../../../application/common/utils/kpi-helpers";
import { LOADING_OVERLAY_SX } from "../kpi-shared-styles";
import DashboardKpiApi from "../../../../infrastructure/api/pages/dashboard-kpi-api";
import { TimeGranularitySegmentedControl } from "../TimeGranularitySegmentedControl/TimeGranularitySegmentedControl";
import { KpiDetailDialog } from "./KpiDetailDialog";
import "./KpiChartCard.scss";
const COLORS = {
    targetAchieved: "#2AA241",
    targetPartiallyAchieved: "#F09A3E",
    targetNotAchieved: "#DC0D15",
    remainder: "#E9ECEF",
};
const CHART_AREA = { width: "80%", height: "72%" };
const AXIS_TEXT_STYLE = { color: "#6C757D", fontSize: 12 };
const AXIS_TITLE_STYLE = { color: "#6C757D", italic: false, fontSize: 12 };
const V_AXIS_GRID = {
    baselineColor: "#E9ECEF",
    gridlines: { color: "#F1F3F5" },
    minorGridlines: { count: 0 },
};
export const KpiChartCard = ({ title, yAxisLabel, chartType, granularity, onGranularityChange, stackedData = [], dayData = [], loading = false, onPrevPeriod, onNextPeriod, showNavArrows = false, dayWeek, dayYear, filterStartDate, filterEndDate, }) => {
    const { t: translate } = useTranslation();
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailTitle, setDetailTitle] = useState("");
    const [detailTarget, setDetailTarget] = useState(0);
    const [detailAchieved, setDetailAchieved] = useState(0);
    const [detailDateLabel, setDetailDateLabel] = useState("");
    const [detailRows, setDetailRows] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailIsDay, setDetailIsDay] = useState(false);
    const stackedDataRef = useRef(stackedData);
    stackedDataRef.current = stackedData;
    const dayDataRef = useRef(dayData);
    dayDataRef.current = dayData;
    const granularityRef = useRef(granularity);
    granularityRef.current = granularity;
    const filterStartRef = useRef(filterStartDate);
    filterStartRef.current = filterStartDate;
    const filterEndRef = useRef(filterEndDate);
    filterEndRef.current = filterEndDate;
    const isDayView = granularity === "day";
    // ─── Chart Data ────────────────────────────────────────────────────────────
    const dayChartData = useMemo(() => {
        if (!isDayView || !dayData.length)
            return null;
        const headers = [
            "Weekday",
            yAxisLabel,
            { type: "string", role: "tooltip", p: { html: true } },
        ];
        const rows = dayData.map((d) => {
            let dateStr = "";
            if (d.date) {
                const { day, month, year } = parseISODateParts(d.date);
                dateStr = `${String(day).padStart(2, "0")} ${SHORT_MONTH_NAMES[month]} ${year}`;
            }
            const heading = dateStr ? `${d.weekday}, ${dateStr}` : d.weekday;
            const tooltipHtml = buildTooltip(heading, `${yAxisLabel}: ${d.value}`);
            return [d.weekday, d.value, tooltipHtml];
        });
        return [headers, ...rows];
    }, [isDayView, dayData, yAxisLabel]);
    const stackedChartData = useMemo(() => {
        if (isDayView || !stackedData.length)
            return null;
        const headers = [
            "Period",
            "Target Achieved",
            { type: "string", role: "tooltip", p: { html: true } },
            "Target Partially Achieved",
            { type: "string", role: "tooltip", p: { html: true } },
            "Target Not Achieved",
            { type: "string", role: "tooltip", p: { html: true } },
            "Remainder",
            { type: "string", role: "tooltip", p: { html: true } },
        ];
        const rows = stackedData.map((d) => {
            const target = d.target || 0;
            const achieved = d.actual || 0;
            const start = clampedISO(d.periodStart, filterStartDate, filterEndDate);
            const end = clampedISO(d.periodEnd, filterStartDate, filterEndDate);
            const heading = `${d.label} (${formatISOToShortDateNoYear(start)} - ${formatISOToShortDateNoYear(end)})`;
            const tooltipHtml = buildTooltip(heading, `Target: ${target}`, `Achieved: ${achieved}`);
            return [
                d.label,
                d.targetAchieved || 0, tooltipHtml,
                d.targetPartiallyAchieved || 0, tooltipHtml,
                d.targetNotAchieved || 0, tooltipHtml,
                d.remainder || 0, tooltipHtml,
            ];
        });
        return [headers, ...rows];
    }, [isDayView, stackedData, granularity, filterStartDate, filterEndDate]);
    // ─── Chart Options ─────────────────────────────────────────────────────────
    const hAxisTitle = useMemo(() => {
        if (isDayView)
            return "Weekday";
        if (granularity === "year")
            return "Years";
        if (granularity === "month")
            return "Months";
        return "Week Number";
    }, [isDayView, granularity]);
    const stackedOptions = useMemo(() => ({
        isStacked: true,
        legend: "none",
        chartArea: CHART_AREA,
        bar: { groupWidth: "60%" },
        hAxis: { title: hAxisTitle, textStyle: AXIS_TEXT_STYLE, titleTextStyle: AXIS_TITLE_STYLE },
        vAxis: Object.assign(Object.assign({ title: yAxisLabel, minValue: 0 }, V_AXIS_GRID), { textStyle: Object.assign(Object.assign({}, AXIS_TEXT_STYLE), { fontSize: 11 }), titleTextStyle: AXIS_TITLE_STYLE }),
        colors: [COLORS.targetAchieved, COLORS.targetPartiallyAchieved, COLORS.targetNotAchieved, COLORS.remainder],
        tooltip: { isHtml: true, trigger: "focus" },
    }), [hAxisTitle, yAxisLabel]);
    const dayChartOptions = useMemo(() => ({
        legend: "none",
        chartArea: CHART_AREA,
        bar: { groupWidth: "55%" },
        hAxis: { title: "Weekday", textStyle: AXIS_TEXT_STYLE, titleTextStyle: AXIS_TITLE_STYLE },
        vAxis: Object.assign(Object.assign({ title: yAxisLabel, minValue: 0 }, V_AXIS_GRID), { textStyle: Object.assign(Object.assign({}, AXIS_TEXT_STYLE), { fontSize: 11 }), titleTextStyle: AXIS_TITLE_STYLE }),
        colors: ["#009DDC"],
        tooltip: { isHtml: true, trigger: "focus" },
    }), [yAxisLabel]);
    // ─── Drill-down ────────────────────────────────────────────────────────────
    const fetchDetail = useCallback((periodStart, periodEnd) => __awaiter(void 0, void 0, void 0, function* () {
        const fStart = filterStartRef.current;
        const fEnd = filterEndRef.current;
        const clampedStart = fStart ? clampDate(periodStart, fStart, periodEnd) : periodStart;
        const clampedEnd = fEnd ? clampDate(periodEnd, periodStart, fEnd) : periodEnd;
        setDetailLoading(true);
        try {
            const res = yield DashboardKpiApi.getChartDetail(clampedStart, clampedEnd, chartType);
            const data = res.data;
            setDetailTarget(data.target);
            setDetailAchieved(data.achieved);
            setDetailDateLabel(data.dateLabel);
            setDetailRows(data.rows);
        }
        catch (_a) {
            setDetailRows([]);
        }
        finally {
            setDetailLoading(false);
        }
    }), [chartType]);
    const handleBarSelect = useCallback((chartWrapper, isDay) => {
        const chart = chartWrapper.getChart();
        const selection = chart.getSelection();
        if (!(selection === null || selection === void 0 ? void 0 : selection.length) || selection[0].row == null)
            return;
        const rowIndex = selection[0].row;
        const currentDayData = dayDataRef.current;
        const currentStackedData = stackedDataRef.current;
        const currentGranularity = granularityRef.current;
        setDetailIsDay(isDay);
        if (isDay) {
            const dayPoint = currentDayData[rowIndex];
            if (!dayPoint)
                return;
            setDetailTitle(`${title} - ${translate("Day")} ${dayPoint.weekday}`);
            if (dayPoint.date) {
                const start = extractDateFromISO(dayPoint.date);
                fetchDetail(start, start);
            }
            else {
                setDetailTarget(dayPoint.value);
                setDetailAchieved(dayPoint.value);
                setDetailDateLabel("");
                setDetailRows([]);
            }
        }
        else {
            const point = currentStackedData[rowIndex];
            if (!point)
                return;
            const fStart = filterStartRef.current;
            const fEnd = filterEndRef.current;
            const dispStart = clampedISO(point.periodStart, fStart, fEnd);
            const dispEnd = clampedISO(point.periodEnd, fStart, fEnd);
            let detailLabel = point.label;
            if (currentGranularity === "week") {
                const weekNum = point.label.replace(/\D.*$/, "");
                detailLabel = `${translate("Week")} ${weekNum} (${formatISOToShortDate(dispStart)} - ${formatISOToShortDate(dispEnd)})`;
            }
            else if (currentGranularity === "month") {
                detailLabel = `${point.label} (${formatISOToShortDate(dispStart)} - ${formatISOToShortDate(dispEnd)})`;
            }
            setDetailTitle(`${title} - ${detailLabel}`);
            fetchDetail(extractDateFromISO(point.periodStart), extractDateFromISO(point.periodEnd));
        }
        setDetailOpen(true);
    }, [title, translate, fetchDetail]);
    const dayChartEvents = useMemo(() => [
        { eventName: "select", callback: ({ chartWrapper }) => handleBarSelect(chartWrapper, true) },
    ], [handleBarSelect]);
    const stackedChartEvents = useMemo(() => [
        { eventName: "select", callback: ({ chartWrapper }) => handleBarSelect(chartWrapper, false) },
    ], [handleBarSelect]);
    // ─── Render ────────────────────────────────────────────────────────────────
    return (_jsxs(Box, { className: "kpi-chart-card", children: [_jsxs(Box, { className: "kpi-chart-header", children: [_jsx(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: _jsx(Typography, { className: "kpi-chart-title", children: title }) }), _jsx(TimeGranularitySegmentedControl, { value: granularity, onChange: onGranularityChange })] }), showNavArrows && isDayView && (_jsxs(Box, { className: "kpi-chart-week-filter", children: [_jsx(Typography, { component: "span", className: "kpi-chart-week-filter-label", children: translate("FilterThroughWeeks") }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsxs(Box, { className: "kpi-chart-nav-arrows", children: [_jsx("button", { type: "button", onClick: onPrevPeriod, "aria-label": "Previous period", children: _jsx(CaretLeft, { size: 18 }) }), _jsx("button", { type: "button", onClick: onNextPeriod, "aria-label": "Next period", children: _jsx(CaretRight, { size: 18 }) })] }), dayWeek !== undefined && (_jsxs(Typography, { variant: "body2", sx: { fontWeight: 500 }, children: [translate("Week"), " ", dayWeek, dayYear && dayYear !== new Date().getFullYear() ? ` '${String(dayYear).slice(2)}` : ""] }))] })] })), !isDayView && (_jsxs(Box, { className: "kpi-legend", children: [_jsxs("span", { className: "legend-item", children: [_jsx("span", { className: "legend-dot green" }), translate("TargetAchieved")] }), _jsxs("span", { className: "legend-item", children: [_jsx("span", { className: "legend-dot orange" }), translate("TargetPartiallyAchieved")] }), _jsxs("span", { className: "legend-item", children: [_jsx("span", { className: "legend-dot red" }), translate("TargetNotAchieved")] }), _jsxs("span", { className: "legend-item", children: [_jsx("span", { className: "legend-dot grey" }), translate("UnmetExcess")] })] })), _jsxs(Box, { className: "kpi-chart-container", sx: { position: "relative" }, children: [loading && (_jsx(Box, { sx: LOADING_OVERLAY_SX, children: _jsx(CircularProgress, { size: 28, color: "inherit" }) })), isDayView && dayChartData ? (_jsx(Chart, { chartType: "ColumnChart", width: "100%", height: "300px", data: dayChartData, options: dayChartOptions, chartEvents: dayChartEvents }, "day-chart")) : stackedChartData ? (_jsx(Chart, { chartType: "ColumnChart", width: "100%", height: "300px", data: stackedChartData, options: stackedOptions, chartEvents: stackedChartEvents }, "stacked-chart")) : !loading ? (_jsxs(Box, { sx: {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "300px",
                            color: "#ADB5BD",
                            gap: 1,
                        }, children: [_jsx(ChartBar, { size: 48, weight: "thin" }), _jsx(Typography, { variant: "body1", sx: { fontWeight: 500, color: "#6C757D" }, children: translate("NoDataAvailable") }), _jsx(Typography, { variant: "caption", sx: { color: "#ADB5BD" }, children: translate("AdjustDateRange") })] })) : null] }), _jsx(KpiDetailDialog, { open: detailOpen, onClose: () => setDetailOpen(false), title: detailTitle, target: detailTarget, achieved: detailAchieved, dateLabel: detailDateLabel, rows: detailRows, loading: detailLoading, hideTarget: detailIsDay })] }));
};
// ─── Helpers ─────────────────────────────────────────────────────────────────
function clampedISO(iso, filterStart, filterEnd) {
    const date = extractDateFromISO(iso);
    if (filterStart && date < filterStart)
        return filterStart + "T00:00:00";
    if (filterEnd && date > filterEnd)
        return filterEnd + "T00:00:00";
    return iso;
}
function buildTooltip(heading, ...lines) {
    const body = lines.map((l) => `<div>${l}</div>`).join("\n  ");
    return `<div style="padding:8px 12px;color:#fff;background:#1f1f23;border-radius:8px;font-size:12px;white-space:nowrap;box-shadow:0px 4px 10px 0px #0000001F">
  <div style="font-weight:600;margin-bottom:4px">${heading}</div>
  ${body}
</div>`;
}
